import React, { useState } from 'react';
import GenericDialog from 'src/components/GenericDialog';
import DynamicFormWidget from 'src/components/dynamic-widget/DynamicFormWidget';
import { DynamicField } from 'src/components/dynamic-widget';
import { toast } from 'react-toastify';
import { ShipmentStatus, Shipment } from 'src/types/Shipment.types';
import { DataService } from 'src/services/data-service';
import { IDynamicSelectField, IDynamiDatePickerField } from 'src/components/dynamic-widget';
import GenericFileInput from 'src/components/input-components/GenericFileInput';
import CloseIcon from '@mui/icons-material/Close';

interface UpdateShipmentDrawerProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  shipment: Shipment | null;
}

const UpdateShipmentDrawer: React.FC<UpdateShipmentDrawerProps> = ({ open, onClose, onSuccess, shipment }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);

  if (!shipment) return null;

  const statusEnumEntries = Object.entries(ShipmentStatus).map(([k, v], idx) => [idx, k]);

  const fields: Record<string, IDynamicSelectField | IDynamiDatePickerField> = {
    Status: {
      type: DynamicField.SELECT,
      required: true,
      name: 'Status',
      title: 'Status',
      items: statusEnumEntries.reduce((acc, [num, label]) => {
        acc[String(num)] = String(label).replace(/([A-Z])/g, ' $1').trim();
        return acc;
      }, {} as Record<string, string>),
      value: String(Object.values(ShipmentStatus).indexOf(shipment.status)),
      disabled: false,
    },
    ArrivalDate: {
      type: DynamicField.DATE,
      required: false,
      name: 'ArrivalDate',
      title: 'Arrival Date (optional)',
      value: shipment.actualArrivalDate || '',
      disabled: false,
    },
  };

  const handleSubmit = async (data: Record<string, any>) => {
    setLoading(true);
    setError(null);
    try {
      const statusNum = Number(data.Status);
      const statusValue = Object.values(ShipmentStatus)[statusNum];
      const payload = {
        Id: shipment.id,
        Status: statusNum,
        ArrivalDate: data.ArrivalDate
      }
      const res = await DataService.post('api/shipment/update', payload);
      if (res.ok) {
        toast.success('Shipment updated!');
        onSuccess();
        onClose();
        return true;
      } else {
        const errData = await res.json();
        setError(errData?.message || 'Failed to update shipment');
        toast.error(errData?.message || 'Failed to update shipment');
        return false;
      }
    } catch (err: any) {
      setError('Failed to update shipment');
      toast.error('Failed to update shipment');
      return false;
    } finally {
      setLoading(false);
    }
  };

  return (
    <GenericDialog title="Update Shipment" open={open} onClose={onClose}>
      {error && (
        <div style={{ background: '#fdecea', color: '#b71c1c', padding: 12, borderRadius: 8, marginBottom: 8, display: 'flex', alignItems: 'center', position: 'relative' }}>
          <span style={{ flex: 1 }}>{error}</span>
          <span style={{ cursor: 'pointer', marginLeft: 8 }} onClick={() => setError(null)}>
            <CloseIcon fontSize="small" />
          </span>
        </div>
      )}
      <DynamicFormWidget
        title=""
        drawerMode
        fields={fields}
        onSubmit={handleSubmit}
        initialValues={{ Status: String(Object.values(ShipmentStatus).indexOf(shipment.status)), ArrivalDate: shipment.actualArrivalDate || '' }}
      />
    </GenericDialog>
  );
};

export default UpdateShipmentDrawer; 